import S from 's-js';
import SArray from 's-array';
import * as _ from 'underscore';
import TZDate from 'lib/TZDate';
import { IAjax } from 'lib/Ajax';
import { debounceSignal } from 'lib/debounceSignal';
import { tzmoment } from 'lib/tzmoment';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { Entrypoints } from 'core/app/entrypoints';
import { BatchesDashboardData } from 'data/models/BatchesDashboard';
import { RecurringBatch } from 'data/models/RecurringBatch';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { PortCallUserRole } from 'server/enums/PortCallUserRole';
import { UserSummary } from 'data/models/UserSummary';
import { serializeDate } from 'common/services/routeParams';
import { BatchesDashboardCache } from './cache';
import { toCSVDataUri, CSVField, ExcelDateFormat, ExcelTimeFormat } from 'common/services/csvExport';
import { GridColumn, RowSorter } from 'portcall/gridcomponents/model';
import { DateRangeSelector, DateRangeSelectorParams, DateRange } from 'portcall/daterangeselector/model';
import { serializeDateRange } from 'portcall/daterangeselector/routeParams';

// TODO: Localization!!
const DATE_FORMAT = 'MM/DD/YYYY',
    DATE_TIME_FORMAT = 'MM/DD/YYYY [at] HH:mm';

const
    VesselName : GridColumn<BatchViewModel> = {
        Title: 'Vessel',
        DisplayValue: batch => batch.VesselName(),
        SortValue:    batch => batch.VesselName(),
        ClassName: 'vessel'
    },
    BerthName : GridColumn<BatchViewModel> = {
        Title: 'Berth',
        DisplayValue: batch => batch.BerthName(),
        SortValue:    batch => batch.BerthName(),
        ClassName: 'berth'
    },
    PortName : GridColumn<BatchViewModel> = {
        Title: 'Port/Region',
        DisplayValue: batch => batch.PortName(),
        SortValue:    batch => batch.PortName(),
        ClassName: 'port'
    },
    RequestDate : GridColumn<BatchViewModel> = {
        Title: 'Date Ordered',
        DisplayValue: batch => batch.DateOrderedDisplay(),
        SortValue:    batch => batch.Model.RequestDate(),
        ClassName: 'date-ordered'
    },
    FirstReservationDate : GridColumn<BatchViewModel> = {
        Title: 'First Reservation',
        DisplayValue: batch => batch.FirstReservationDateDisplay(),
        SortValue:    batch => batch.FirstReservationDate() || new TZDate(0),
        ClassName: 'first-reservation'
    },
    LastReservationDate : GridColumn<BatchViewModel> = {
        Title: 'Last Reservation',
        DisplayValue: batch => batch.LastReservationDateDisplay(),
        SortValue:    batch => batch.LastReservationDate() || new TZDate(0),
        ClassName: 'last-reservation'
    },
    Count : GridColumn<BatchViewModel> = {
        Title: 'Count',
        DisplayValue: batch => batch.Model.PortCalls().length + '',
        SortValue:    batch => batch.Model.PortCalls().length,
        ClassName: 'count'
    },
    ReferenceNumber : GridColumn<BatchViewModel> = {
        Title: 'Reference #',
        DisplayValue: batch => batch.Model.ReferenceNumber(),
        SortValue:    batch => batch.Model.ReferenceNumber(),
        ClassName: 'reference-number'
    };

const DefaultSortColumn = FirstReservationDate;

export const Columns = [
    VesselName,
    BerthName,
    PortName,
    RequestDate,
    FirstReservationDate,
    LastReservationDate,
    Count,
    ReferenceNumber
];

const CSVColumns : CSVField<BatchViewModel>[] = [{
    name: VesselName.Title,
    formatter: VesselName.DisplayValue
}, {
    name: BerthName.Title,
    formatter: BerthName.DisplayValue
}, {
    name: PortName.Title,
    formatter: PortName.DisplayValue
}, {
    name: RequestDate.Title,
    formatter: batch => ExcelDateFormat(batch.Model.RequestDate())
}, {
    name: 'First Reservation (Date)',
    formatter: batch => batch.FirstReservationDate() ? ExcelDateFormat(batch.FirstReservationDate()!) : ''
}, {
    name: 'First Reservation (Time)',
    formatter: batch => batch.FirstReservationDate() ? ExcelTimeFormat(batch.FirstReservationDate()!) : ''
}, {
    name: 'Last Reservation (Date)',
    formatter: batch => batch.LastReservationDate() ? ExcelDateFormat(batch.LastReservationDate()!) : ''
}, {
    name: 'Last Reservation (Time)',
    formatter: batch => batch.LastReservationDate() ? ExcelTimeFormat(batch.LastReservationDate()!) : ''
}, {
    name: Count.Title,
    formatter: Count.DisplayValue
}, {
    name: ReferenceNumber.Title,
    formatter: ReferenceNumber.DisplayValue
}];

export const DefaultRole = PortCallUserRole.Any;
const filterStorageKey = 'portcall.batchesdashboard.filter';

export interface BatchViewModel {
    Model : RecurringBatch;
    VesselName : () => string;
    BerthName : () => string;
    PortName : () => string;
    DateOrderedDisplay : () => string;
    FirstReservationDate : () => TZDate | null;
    FirstReservationDateDisplay : () => string;
    LastReservationDate : () => TZDate | null;
    LastReservationDateDisplay : () => string;
    FilterData : () => string;
    OnRowClick : () => void;
}

export interface BatchesDashboardParams {
    orgId? : number;
    range : DateRange;
    role? : PortCallUserRole;
    from? : TZDate;
    to? : TZDate;
    filter? : string;
}

export function BatchesDashboard(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : BatchesDashboardCache,
    user : () => UserSummary | null,
    dateRangeSelectorModel : (params : DateRangeSelectorParams) => DateRangeSelector,
    { orgId: _orgId, range, from, to, role: _role, filter: _filter } : BatchesDashboardParams
) {
    const orgId = _orgId !== undefined ? _orgId : user() ? user()!.OrganizationId() : 0,
        role = S.data(_role !== undefined ? _role : DefaultRole),
        dateRangeSelector = dateRangeSelectorModel({ range, from, to }),
        loading = S.data(true),
        key = () => ({
            OrganizationId: orgId,
            FromDate: dateRangeSelector.FromDate().toJSON(),
            ToDate: dateRangeSelector.ToDate().toJSON(),
            Role: role()
        }),
        model = S(() => cache.BatchesDashboards.update1(null, key())),
        rowSorter = RowSorter(DefaultSortColumn),
        filter = S.data(_filter || localStorage[filterStorageKey] as string | undefined || ''),
        debouncedFilter = debounceSignal(filter),
        batches = SArray<RecurringBatch>([]),
        sortedBatches = batches.map(BatchViewModel)
            .filter(batch => CaseInsensitiveContains(batch.FilterData(), debouncedFilter()))
            .sort(rowSorter.RowOrder),
        showActionsMenu = S.data(false),
        csvExportLink = S(() => toCSVDataUri(sortedBatches(), CSVColumns));

    // Store filter
    S(() => {
        localStorage[filterStorageKey] = filter();
    });

    // Fetch data
    S(() => {
        loading(true);
        let req = ajax.Url<BatchesDashboardData>(entrypoints.BatchesDashboard(key())).Get();
        req.then(json => cache.BatchesDashboards.update1(req.Date, json))
            .finally(() => loading(false));
    });

    S(() => batches(model().Batches()));

    return {
        OrgId: orgId,
        Role: role,
        Organization: () => model().Organization(),
        Batches: sortedBatches,
        DateRangeSelector: dateRangeSelector,
        Filter: filter,
        Loading: loading,
        RowSorter: rowSorter,
        ShowActionsMenu: showActionsMenu,
        CSVExportLink: csvExportLink,
        GoBack: () => window.history.back()
    };

    function BatchViewModel(batch : RecurringBatch) : BatchViewModel {
        const firstPoint = S(() => batch.FirstReservationPoint()),
            lastPoint = S(() => batch.LastReservationPoint()),
            firstPortCall = S(() => batch.FirstPortCall()),
            vesselName = () => firstPortCall() && firstPortCall()!.DisplayVesselIdentifier() || '--',
            berthName = () => firstPoint() && firstPoint()!.Location() && firstPoint()!.Location()!.Name() || '--',
            portName = () => firstPortCall() && firstPortCall()!.Port() && firstPortCall()!.Port()!.Name() || '--',
            dateOrderedDisplay = () => tzmoment(batch.RequestDate()).format(DATE_FORMAT),
            firstReservationDate = () => firstPoint() && firstPoint()!.Arrival.Effective(),
            firstReservationDateDisplay = () => firstReservationDate() ? tzmoment(firstReservationDate()!).format(DATE_TIME_FORMAT) : '--',
            lastReservationDate = () => lastPoint() && lastPoint()!.Arrival.Effective(),
            lastReservationDateDisplay = () => lastReservationDate() ? tzmoment(lastReservationDate()!).format(DATE_TIME_FORMAT) : '--',
            filterData = () =>
                vesselName() + ' ' +
                berthName() + ' ' +
                portName() + ' ' +
                dateOrderedDisplay() + ' ' +
                firstReservationDateDisplay() + ' ' +
                lastReservationDateDisplay() + ' ' +
                batch.PortCalls().length + ' ' +
                batch.ReferenceNumber();

        return {
            Model: batch,
            VesselName: vesselName,
            BerthName: berthName,
            PortName: portName,
            DateOrderedDisplay: dateOrderedDisplay,
            FirstReservationDate: firstReservationDate,
            FirstReservationDateDisplay: firstReservationDateDisplay,
            LastReservationDate: lastReservationDate,
            LastReservationDateDisplay: lastReservationDateDisplay,
            FilterData: filterData,
            OnRowClick: () => window.location.hash = `#!reservationsinbatch?batchId=${batch.Id()}`
        };
    }
}
export type BatchesDashboard = ReturnType<typeof BatchesDashboard>;



// WEBPACK FOOTER //
// ./apps/portcall/batchesdashboard/model.ts